import React, { useState } from "react";
import Fuse from 'fuse.js';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import swal from '@sweetalert/with-react';
import { API } from "aws-amplify";
import { FileUpload } from "../../../../js/upload";

export const CreateCourseMap = ({
  scheduleData: { courses, schoolInfo },
  magnetboardActions: { pullNewSectionData },
}) => {
  const [courseMap, setCourseMap] = useState({});
  const [gymCourseSelected, setGymCourseSelected] = useState(null);
  const [courseTypeOpen, setCourseTypeOpen] = useState(null);
  const [searchResults, setSearchResults] = useState([...courses]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const fuzzySearch = (e) => {
    const search_value = e.target.value;

    const fuse = new Fuse(courses, {
      keys: ["name", "course_code"],
      threshold: 0.1,
    });

    const results = fuse.search(search_value);
    const course_results = results.map((result) => result.item).slice(0, 6);

    const search_results =
      search_value.trim().length === 0 ? courses : course_results;
    setSearchResults([...search_results]);
  };

  const addToCourseMap = (course_id) => {
    if (courseTypeOpen === "gym") {
      const course_already_exists =
        Object.keys(courseMap).findIndex(
          (gym_course_id) => courseMap[gym_course_id] === gym_course_id
        ) !== -1;
      if (!course_already_exists) courseMap[course_id] = null;
    } else if (courseTypeOpen === "health" && gymCourseSelected) {
      if (gymCourseSelected in courseMap)
        courseMap[gymCourseSelected] = course_id;
    }

    setCourseMap({ ...courseMap });
    setGymCourseSelected(null);
    setCourseTypeOpen(null);
    setSearchResults([...courses]);
    setErrorMessage(null);
  };

  const removeFromCourseMap = (gym_course_id) => {
    delete courseMap[gym_course_id];

    setCourseMap({ ...courseMap });
    setErrorMessage(null);
  };

  const submitCourseMap = async () => {
    if (Object.keys(courseMap).length === 0) {
      setErrorMessage("You need to add a Phys Ed & Health pairing!");
      return false;
    }

    let health_is_missing = false;
    Object.keys(courseMap).forEach((gym_course_id) => {
      const health_course_id = courseMap[gym_course_id];
      if (!health_course_id) health_is_missing = true;
    });

    if (health_is_missing) {
      setErrorMessage(
        "There is a Phys Ed/Gym course missing a Health course pairing!"
      );
      return false;
    }

    setIsGenerating(true);

    const data = {
      school_id: schoolInfo.school_id,
      schedule_version_id: schoolInfo.current_schedule_version_id,
      data_version_id: schoolInfo.current_data_version_id,
      courseMap: courseMap,
    };
    const apiName = process.env.REACT_APP_ENDPOINT_NAME;
    const url = "/place-health";
    const myInit = {
      // OPTIONAL
      response: true,
      body: JSON.stringify(data),
    };

    try {
      await API.post(apiName, url, myInit);

      const fileUpload = new FileUpload(
        schoolInfo.current_data_version_id,
        schoolInfo.current_schedule_version_id,
        schoolInfo.school_id
      );
      const running_processes = await fileUpload.getSchoolProcesses(
        schoolInfo.current_schedule_year_id
      );

      // Check if there are any running processes with the same data version id
      const process_index = running_processes.findIndex(
        (process) =>
          process.process_type === "healthplace" &&
          process.schedule_version_id === schoolInfo.current_schedule_version_id
      );

      // If process is already running, get progress of matches
      const process_info =
        process_index !== -1 ? running_processes[process_index] : null;
      const progress_id = process_info ? process_info.progress_id : null;

      await fileUpload.checkUploading(
        schoolInfo.school_id,
        progress_id,
        (event) => {
          if (event.done) {
            if (event.failure) {
              const failure_options = {
                icon: "error",
                title: "Error Creating Health Sections",
                text: "We had an issue creating your health sections. Please try again.",
                dangerMode: true,
              };

              swal(failure_options);
            } else {
              const success_options = {
                icon: "success",
                title: "Success!",
                text: "Your health sections were successfully created.",
              };

              pullNewSectionData(schoolInfo.current_schedule_version_id);
              swal(success_options);
            }
          }
        }
      );
    } catch (e) {
      const failure_options = {
        icon: "error",
        title: "Error Creating Health Sections",
        text: "We had an issue creating your health sections. Please try again.",
        dangerMode: true,
      };

      swal(failure_options);
      console.log(e.response);
    }
  };

  return (
    <div>
      {isGenerating ? (
        <div className="loader"></div>
      ) : (
        <>
          <div className="mb-create-health-map-parent-container text-align-left">
            <div>
              Tell us which Healths should be paired with which Phys Ed/Gym
              courses:
            </div>
            <div className="mb-create-health-map-container">
              {Object.keys(courseMap).length > 0 && (
                <>
                  <h2 className="mb-create-health-map-header">
                    Phys Ed/Gym Course
                  </h2>
                  <div></div>
                  <h2 className="mb-create-health-map-header">Health Course</h2>
                  <div></div>
                </>
              )}
              {Object.keys(courseMap).map((gym_course_id, i) => {
                const health_course_id = courseMap[gym_course_id];

                const gym_course_info = courses.find(
                  (course) => course.course_id === gym_course_id
                );
                const health_course_info = health_course_id
                  ? courses.find(
                      (course) => course.course_id === health_course_id
                    )
                  : null;

                return (
                  <React.Fragment key={i}>
                    <div className="mb-create-health-map-pair medium-text">
                      {gym_course_id && (
                        <span>
                          {gym_course_info.name} ({gym_course_info.course_code})
                        </span>
                      )}
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} />
                    <div className="mb-create-health-map-pair medium-text">
                      {health_course_id ? (
                        <span>
                          {health_course_info.name} (
                          {health_course_info.course_code})
                        </span>
                      ) : (
                        <div
                          className="mb-create-health-map-link blue-link"
                          onClick={() => {
                            setGymCourseSelected(gym_course_id);
                            setCourseTypeOpen("health");
                            setSearchResults([...courses]);
                          }}
                        >
                          Add Health Pairing
                        </div>
                      )}
                    </div>
                    <FontAwesomeIcon
                      className="gray-to-red-link"
                      icon={faTimes}
                      onClick={() => removeFromCourseMap(gym_course_id)}
                    />
                  </React.Fragment>
                );
              })}
            </div>
            <div
              className="mb-create-health-map-btn btn turquoise-btn"
              onClick={() => {
                setCourseTypeOpen("gym");
                setSearchResults([...courses]);
              }}
            >
              Add New Pairing
            </div>
            {courseTypeOpen && (
              <div className="mb-create-health-map-search-container">
                <FontAwesomeIcon
                  className="x-cancel mb-create-health-map-search-add-cancel-new-btn"
                  icon={faTimes}
                  onClick={() => setCourseTypeOpen(null)}
                />
                <div className="mb-create-health-map-search-add-did-you-mean">
                  Search for a{" "}
                  <span className="capitalize">{courseTypeOpen}</span> class:
                </div>
                <div className="search-add-adding-input-container">
                  <input
                    id="main-course-input"
                    className="edit-section-input"
                    placeholder={`Search for a ${courseTypeOpen} course`}
                    onChange={(e) => fuzzySearch(e, "main", "courses")}
                    autoFocus={true}
                  />
                  <FontAwesomeIcon
                    className="search-add-adding-input-icon"
                    icon={faSearch}
                  />
                </div>
                {searchResults.length !== 0 && (
                  <>
                    <div className="mb-create-health-map-search-add-search-results">
                      {searchResults.map((course, i) => {
                        return (
                          <div className="search-add-search-result" key={i}>
                            <div>
                              {course.name} ({course.course_code})
                            </div>
                            <div>
                              <div
                                className="search-add-add-btn"
                                onClick={() => addToCourseMap(course.course_id)}
                              >
                                Select
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {errorMessage && (
            <div className="error-message-no-box">{errorMessage}</div>
          )}
          <div className="swal-footer">
            <div className="swal-button-container" onClick={() => swal.close()}>
              <button
                className="swal-button swal-button--cancel gray-btn"
                tabIndex="0"
              >
                Cancel
              </button>
              <div className="swal-button__loader"></div>
            </div>
            <div className="swal-button-container" onClick={submitCourseMap}>
              <button className="swal-button blue-btn">Create Healths</button>
              <div className="swal-button__loader"></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
